import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  // [scroll top after cart drawer close - start]
  /** @type {ScrollRestoration | null} */
  #previousScrollRestoration = null;
  // [scroll top after cart drawer close - end]

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      // [scroll top after cart drawer close - start]
      // scrollRestoration is stored per history entry. Turn it off on the
      // page's own entry *before* pushing the drawer entry, otherwise going
      // back restores that entry's saved position — the top, because the page
      // was scroll-locked when the drawer opened.
      if (this.#previousScrollRestoration === null) {
        this.#previousScrollRestoration = history.scrollRestoration;
      }
      history.scrollRestoration = 'manual';
      // [scroll top after cart drawer close - end]
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      // [scroll top after cart drawer close - start]
      // The history entry was saved while the page was scroll-locked (at the
      // top), so history.back() would jump there. Keep the position the
      // dialog just restored instead.
      const scrollY = window.scrollY;
      // Replaced: setting scrollRestoration here only affected the drawer's
      // own history entry, not the page entry history.back() returns to.
      // const previousRestoration = history.scrollRestoration;
      // history.scrollRestoration = 'manual';
      window.addEventListener(
        'popstate',
        () => {
          window.scrollTo({ top: scrollY, behavior: 'instant' });
          // history.scrollRestoration = previousRestoration;
          // [scroll top after cart drawer close - start]
          // this.#restoreScrollRestoration(); // old: too early for iPhone Safari
          this.#holdScrollPosition(scrollY);
          // [scroll top after cart drawer close - end]
        },
        { once: true }
      );
      // [scroll top after cart drawer close - end]
      history.back();
    }
    // [scroll top after cart drawer close - start]
    else {
      // Closed via the phone's Back button: the history step already happened.
      this.#restoreScrollRestoration();
    }
    // [scroll top after cart drawer close - end]
  };

  // [scroll top after cart drawer close - start]
  // iPhone Safari restores its own (wrong) position a moment after popstate.
  // For ~500ms keep putting the page back where the shopper was, then switch
  // the browser's normal scroll restore back on. Stops at once if the
  // shopper touches or scrolls.
  #holdScrollPosition(targetY) {
    let stopped = false;
    const stop = () => {
      stopped = true;
    };
    window.addEventListener('touchstart', stop, { once: true, passive: true });
    window.addEventListener('wheel', stop, { once: true, passive: true });

    const correct = () => {
      if (!stopped && Math.abs(window.scrollY - targetY) > 1) {
        window.scrollTo({ top: targetY, behavior: 'instant' });
      }
    };
    requestAnimationFrame(correct);
    [50, 150, 300, 500].forEach((ms) => setTimeout(correct, ms));

    setTimeout(() => {
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('wheel', stop);
      // Drawer reopened meanwhile: keep restore off until that one closes.
      if (history.state?.cartDrawerOpen) return;
      this.#restoreScrollRestoration();
    }, 600);
  }
  // [scroll top after cart drawer close - end]

  // [scroll top after cart drawer close - start]
  #restoreScrollRestoration() {
    if (this.#previousScrollRestoration === null) return;
    history.scrollRestoration = this.#previousScrollRestoration;
    this.#previousScrollRestoration = null;
  }
  // [scroll top after cart drawer close - end]

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  #handleCartAdd = () => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }
  };

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState() {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  }
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}
